import type { ReactNode } from 'react'

import type { NoteSource } from '../../lib/home'

/**
 * "How notes are written", by the stored note's own `source` (R65, 2026-09-30).
 *
 * Part of every stored note, in every build: the owner turns the note model on
 * with this deploy, so the disclosure is no longer a flag. Each sentence says
 * only what that writer does (brainspot-backend `contracts/daily_note.py`: a
 * Bedrock model in eu-west-2 arranges the lexicon's fixed clauses, the validator
 * checks the result before it is stored, and the template replaces it when a
 * check fails). A note whose source is absent or unknown makes no claim about how
 * it was written -- and in particular no claim of AI processing.
 *
 * The model sentence makes NO CLAIM OF APPROVAL (R65 copy version 2): the clauses
 * come from the DRAFT lexicon (`lexicon.APPROVAL` is None, `lexicon/1-draft`), and
 * Enrico reviews them on the deployed dev build. "approved" may come back only
 * once his sign-off is recorded.
 */
const NOTE_DISCLOSURE: Readonly<Record<NoteSource, string>> = {
  template: 'This note was written from a fixed template, using the facts in this reading.',
  model: 'An AI model wrote this note from a fixed set of sentences, using only the facts in this reading. '
    + 'It was checked before it was saved; if a check fails, a fixed template is used instead.',
}
const NOTE_DISCLOSURE_UNKNOWN = 'This note describes the facts in this reading.'

/**
 * The note beside the map (Sprint 1.9.9, R49).
 *
 * Writing the daily note is Sprint 1.9.10. Until `/home.note` carries one, the
 * card shows the reading's own `summary`, verbatim and dated to the reading —
 * with no "daily note" eyebrow, because nothing here was written as one. Every
 * string arrives finished from `Home.tsx`; this is layout. It is the note HALF
 * of the mirror's one reading card, beside the map (fidelity #7), not a card of
 * its own; with flags off it is the card's only half.
 */
export function NoteCard({
  dated,
  headline,
  body,
  stored = false,
  source = null,
  lead,
  compact = false,
  notice,
  chips,
  action,
}: {
  /** "Sep 11, 2026 · Eyes closed"; null when the card's head already dates an older reading. */
  dated: ReactNode | null
  headline: string | null
  body: string | null
  stored?: boolean
  /** The stored note's writer; null when the backend did not say, or said something this build does not know. */
  source?: NoteSource | null
  lead?: ReactNode
  compact?: boolean
  /** An unreadable reading's explanation, in place of the body. */
  notice?: ReactNode
  /** "What moved". */
  chips?: ReactNode
  action: ReactNode
}) {
  return (
    <div className="mirror__note" data-testid="home-note">
      {lead}
      {dated !== null && <p className="mirror__dated">{dated}</p>}
      {stored && <p className="mirror__eyebrow">Your daily note</p>}
      {headline !== null && <h2 className="mirror__headline">{headline}</h2>}
      {notice}
      {body !== null && <p className={compact ? 'mirror__quote' : 'mirror__body'}>{body}</p>}
      {stored && (
        <details className="mirror__disclosure" data-testid="note-disclosure" data-source={source ?? 'unknown'}>
          <summary>How notes are written</summary>
          <p>{source === null ? NOTE_DISCLOSURE_UNKNOWN : NOTE_DISCLOSURE[source]}</p>
        </details>
      )}
      {chips}
      <div className="mirror__actions">{action}</div>
    </div>
  )
}
